import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  whatsappWebService, 
  WhatsAppWebSession 
} from '../services/whatsappWebService';
import { RootState } from '../store/store';

const WhatsAppWebSetup: React.FC = () => {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [session, setSession] = useState<WhatsAppWebSession | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionName, setSessionName] = useState<string>('');
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);

  // Initialize socket connection
  useEffect(() => {
    // Initialize socket but don't store it since we're just using the global instance
    whatsappWebService.initSocket();
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [refreshInterval]);

  // Handle session events
  const handleSessionEvent = useCallback((event: any) => {
    if (event.type === 'qr') {
      setQrCode(event.qr);
    } else if (event.type === 'status') {
      setSession(prev => prev ? { ...prev, status: event.status } : null);
      
      if (event.status === 'authenticated' || event.status === 'ready') {
        setQrCode(null);
      }
      
      if (event.error) {
        setError(`Authentication error: ${event.error}`);
      }
    }
  }, []);

  // Load existing session
  const loadSession = useCallback(async (id: string) => {
    try {
      setError(null);
      
      // Generate session key
      const sessionKey = `${user?.id}_${id}`;
      
      // Get session status
      const sessionData = await whatsappWebService.getSessionStatus(sessionKey);
      setSession(sessionData);
      
      // Set up event listeners
      whatsappWebService.addSessionListener(sessionKey, 'qr', handleSessionEvent);
      whatsappWebService.addSessionListener(sessionKey, 'status', handleSessionEvent);
      
      // If session has QR, fetch it
      if (sessionData.hasQR) {
        try {
          const qr = await whatsappWebService.getSessionQR(sessionKey);
          setQrCode(qr);
        } catch (err) {
          console.log('QR code not available yet');
        }
      }
      
      // Set up refresh interval
      const interval = setInterval(async () => {
        try {
          const updatedSession = await whatsappWebService.getSessionStatus(sessionKey);
          setSession(updatedSession);
        } catch (err) {
          console.error('Error refreshing session status:', err);
        }
      }, 10000); // Refresh every 10 seconds
      
      setRefreshInterval(interval);
      
    } catch (err: any) {
      setError(err.message || 'Error loading session');
      console.error('Error loading session:', err);
    }
  }, [user, handleSessionEvent]);  // Add dependencies for the useCallback
  
  // Load session if sessionId is provided
  useEffect(() => {
    if (sessionId && user) {
      loadSession(sessionId);
    }
  }, [sessionId, user, loadSession]);

  // Create new session
  const createSession = async () => {
    if (!user) {
      setError('You must be logged in to create a WhatsApp Web session');
      return;
    }
    
    if (!sessionName.trim()) {
      setError('Please enter a session name');
      return;
    }
    
    try {
      setIsCreating(true);
      setError(null);
      
      // Create new session and ignore the return value since we're navigating away
      await whatsappWebService.createSession(
        user.id, 
        sessionName.trim()
      );
      
      // Navigate to the session page
      navigate(`/whatsapp-web/${sessionName.trim()}`);
      
    } catch (err: any) {
      setError(err.message || 'Error creating session');
      console.error('Error creating session:', err);
    } finally {
      setIsCreating(false);
    }
  };

  // Delete session
  const deleteSession = async () => {
    if (!session || !sessionId) return;
    
    if (window.confirm('Are you sure you want to delete this WhatsApp Web session?')) {
      try {
        const sessionKey = `${user?.id}_${sessionId}`;
        await whatsappWebService.deleteSession(sessionKey);
        
        // Clean up listeners
        whatsappWebService.removeSessionListener(sessionKey, 'qr', handleSessionEvent);
        whatsappWebService.removeSessionListener(sessionKey, 'status', handleSessionEvent);
        
        // Navigate back to sessions list
        navigate('/whatsapp-web');
        
      } catch (err: any) {
        setError(err.message || 'Error deleting session');
        console.error('Error deleting session:', err);
      }
    }
  };

  // Render QR code section
  const renderQRCode = () => {
    if (!qrCode) return null;
    
    return (
      <div className="mt-6 p-4 bg-white rounded-lg shadow-md">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Scan this QR code with WhatsApp</h3>
        <div className="flex justify-center">
          <img 
            src={qrCode} 
            alt="WhatsApp QR Code" 
            className="w-64 h-64"
          />
        </div>
        <p className="mt-4 text-sm text-gray-500 text-center">
          Open WhatsApp on your phone, tap Menu or Settings and select Linked Devices
        </p>
      </div>
    );
  };

  // Render session status
  const renderSessionStatus = () => {
    if (!session) return null;
    
    let statusColor = 'gray';
    let statusText = 'Unknown';
    
    switch (session.status) {
      case 'initializing':
        statusColor = 'blue';
        statusText = 'Initializing';
        break;
      case 'qr_received':
        statusColor = 'yellow';
        statusText = 'Waiting for QR scan';
        break;
      case 'authenticated':
        statusColor = 'green';
        statusText = 'Authenticated';
        break;
      case 'ready':
        statusColor = 'green';
        statusText = 'Ready';
        break;
      case 'disconnected':
        statusColor = 'red';
        statusText = 'Disconnected';
        break;
      case 'auth_failure':
        statusColor = 'red';
        statusText = 'Authentication Failed';
        break;
    }
    
    return (
      <div className="mt-6 p-4 bg-white rounded-lg shadow-md">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Session Status</h3>
        <div className="flex items-center">
          <div className={`w-3 h-3 rounded-full bg-${statusColor}-500 mr-2`}></div>
          <span className="text-gray-700">{statusText}</span>
        </div>
        
        {session.status === 'ready' && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
            <p className="text-green-700">
              WhatsApp Web is connected and ready to use!
            </p>
          </div>
        )}
        
        {session.status === 'disconnected' && (
          <div className="mt-4">
            <button
              onClick={() => loadSession(sessionId!)}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Reconnect
            </button>
          </div>
        )}
      </div>
    );
  };

  // If no sessionId, show create form
  if (!sessionId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">WhatsApp Web Integration</h1>
        
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Create New WhatsApp Web Session</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-red-700">{error}</p>
            </div>
          )}
          
          <div className="mb-4">
            <label htmlFor="sessionName" className="block text-sm font-medium text-gray-700 mb-1">
              Session Name
            </label>
            <input
              type="text"
              id="sessionName"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Personal WhatsApp"
              disabled={isCreating}
            />
          </div>
          
          <button
            onClick={createSession}
            disabled={isCreating}
            className="px-4 py-2 bg-whatsapp-green text-white rounded hover:bg-whatsapp-dark-green disabled:opacity-50"
          >
            {isCreating ? 'Creating...' : 'Create WhatsApp Web Session'}
          </button>
        </div>
        
        <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">How It Works</h2>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Create a new WhatsApp Web session with a unique name</li>
            <li>Scan the QR code with your WhatsApp mobile app</li>
            <li>Once connected, you can send and receive messages through this system</li>
            <li>Your WhatsApp session will remain active even when you close this browser</li>
          </ol>
          
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-yellow-700 font-medium">Important Notes:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1 text-yellow-700">
              <li>This uses the WhatsApp Web protocol, not the official WhatsApp Business API</li>
              <li>Message templates are not supported with this connection method</li>
              <li>Your phone must be connected to the internet periodically</li>
              <li>For business use, the WhatsApp Business API is recommended</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Show session details
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">WhatsApp Web: {sessionId}</h1>
        <div>
          <button
            onClick={() => navigate('/whatsapp-web')}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 mr-2"
          >
            Back to Sessions
          </button>
          <button
            onClick={deleteSession}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Delete Session
          </button>
        </div>
      </div>
      
      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      
      {renderSessionStatus()}
      {renderQRCode()}
      
      {session?.status === 'ready' && (
        <div className="mt-6 p-4 bg-white rounded-lg shadow-md">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Connection Details</h3>
          <p className="text-gray-700">
            Your WhatsApp Web session is active. You can now send and receive messages through this system.
          </p>
          <div className="mt-4">
            <button
              onClick={() => navigate('/messages')}
              className="px-4 py-2 bg-whatsapp-green text-white rounded hover:bg-whatsapp-dark-green"
            >
              Go to Messages
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppWebSetup;
